import React from "react";
import { ApiResponseMention } from "../../api/types/api-response";
import { ApiTheme } from "../../api/types/api-theme";
import { ApiCategory } from "../../api/types/api-category";
import { Command, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  getNextItemInArray,
  getPreviousItemInArray,
} from "../../utils/array-utils";

const getSentimentColor = (sentiment: ApiResponseMention["sentiment"]) => {
  switch (sentiment) {
    case -1:
      return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-100";
    case 0:
      return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100";
    case 1:
      return "bg-green-100 text-green-800 dark:bg-[rgb(34,74,54)] dark:text-green-100";
  }
};

const getLabel = (
  themeId: number,
  themes: ApiTheme[],
  categoriesMap: Record<string, ApiCategory>
) => {
  const theme = themes.find((theme) => Number(theme.id) === themeId);
  if (!theme) return "";

  const category = categoriesMap[theme.relationships.category.data.id];

  return `${theme?.attributes.name} (${category.attributes.name})`;
};

interface PendingSelection {
  themeId: string;
  sentiment: ApiResponseMention["sentiment"];
}

export const ThemesInputInline: React.FC<{
  mentions: ApiResponseMention[];
  themes: ApiTheme[];
  categories: ApiCategory[];
  onMentionRemove: (mention: ApiResponseMention) => void;
  onMentionAdd: (mention: ApiResponseMention) => void;
}> = (props) => {
  const [inputValue, setInputValue] = React.useState("");
  const [showDropdown, setShowDropdown] = React.useState(false);
  const [focusedThemeIndex, setFocusedThemeIndex] = React.useState(0);
  const [pendingSelection, setPendingSelection] = React.useState<PendingSelection | null>(null);
  const [activeTagIndex, setActiveTagIndex] = React.useState<number | null>(null);
  const [hoveredThemeId, setHoveredThemeId] = React.useState<string | null>(null);
  const [hoverTimeout, setHoverTimeout] = React.useState<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Auto-focus on mount and when mentions change (new response loaded)
  React.useEffect(() => {
    inputRef.current?.focus();
  }, [props.mentions.length > 0 ? props.mentions[0].theme_id : null]);

  const categoriesMap = React.useMemo(() => {
    return props.categories.reduce<Record<string, ApiCategory>>(
      (acc, category) => {
        acc[category.id] = category;
        return acc;
      },
      {}
    );
  }, [props.categories]);

  const availableThemes = React.useMemo(() => {
    return props.themes
      .filter(
        (theme) =>
          !props.mentions.find(
            (mention) => mention.theme_id === Number(theme.id)
          )
      )
      .map((theme) => ({
        id: theme.id,
        label: theme.attributes.name,
        description: theme.attributes.description,
        categoryId: theme.relationships.category.data.id,
        categoryName:
          categoriesMap[theme.relationships.category.data.id].attributes.name,
      }))
      .sort(
        (a, b) =>
          a.categoryName.localeCompare(b.categoryName) ||
          a.label.localeCompare(b.label)
      );
  }, [props.mentions, props.themes, categoriesMap]);

  const filteredThemes = React.useMemo(() => {
    const searchLower = inputValue.toLowerCase();
    const themesToShow = inputValue
      ? availableThemes.filter(
          (theme) =>
            theme.label.toLowerCase().includes(searchLower) ||
            theme.categoryName.toLowerCase().includes(searchLower) ||
            theme.description?.toLowerCase().includes(searchLower)
        )
      : availableThemes;

    return themesToShow;
  }, [availableThemes, inputValue]);

  const maxLabelLength = React.useMemo(() => {
    return filteredThemes.reduce((max, theme) => Math.max(max, theme.label.length), 0);
  }, [filteredThemes]);

  const themesByCategory = React.useMemo(() => {
    const grouped = filteredThemes.reduce<Record<string, typeof filteredThemes>>((acc, theme) => {
      if (!acc[theme.categoryId]) {
        acc[theme.categoryId] = [];
      }
      acc[theme.categoryId].push(theme);
      return acc;
    }, {});

    // Sort categories by name
    return Object.entries(grouped)
      .sort(([, themesA], [, themesB]) =>
        themesA[0].categoryName.localeCompare(themesB[0].categoryName)
      )
      .map(([categoryId, themes]) => ({
        categoryId,
        categoryName: themes[0].categoryName,
        themes: themes.sort((a, b) => a.label.localeCompare(b.label)),
      }));
  }, [filteredThemes]);

  // Reset focused index when filtered themes change
  React.useEffect(() => {
    setFocusedThemeIndex(0);
    setPendingSelection(null);
  }, [filteredThemes.length, inputValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setShowDropdown(value.length > 0);
    setActiveTagIndex(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // If dropdown is showing, handle navigation
    if (showDropdown && filteredThemes.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocusedThemeIndex((prev) =>
          prev < filteredThemes.length - 1 ? prev + 1 : prev
        );
        setPendingSelection(null);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocusedThemeIndex((prev) => (prev > 0 ? prev - 1 : prev));
        setPendingSelection(null);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        const focusedTheme = filteredThemes[focusedThemeIndex];
        if (focusedTheme) {
          const currentSentiment = pendingSelection?.themeId === focusedTheme.id
            ? pendingSelection.sentiment
            : 1;

          const newSentiment = getPreviousItemInArray([1, -1, 0] as const, currentSentiment);
          setPendingSelection({
            themeId: focusedTheme.id,
            sentiment: newSentiment as ApiResponseMention["sentiment"],
          });
        }
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        const focusedTheme = filteredThemes[focusedThemeIndex];
        if (focusedTheme) {
          const currentSentiment = pendingSelection?.themeId === focusedTheme.id
            ? pendingSelection.sentiment
            : 1;

          const newSentiment = getNextItemInArray([1, -1, 0] as const, currentSentiment);
          setPendingSelection({
            themeId: focusedTheme.id,
            sentiment: newSentiment as ApiResponseMention["sentiment"],
          });
        }
      } else if (e.key === "Enter") {
        e.preventDefault();
        const focusedTheme = filteredThemes[focusedThemeIndex];
        if (focusedTheme) {
          const sentiment = pendingSelection?.themeId === focusedTheme.id
            ? pendingSelection.sentiment
            : 1;

          props.onMentionAdd({
            theme_id: Number(focusedTheme.id),
            sentiment,
          });

          setInputValue("");
          setShowDropdown(false);
          setPendingSelection(null);
          setFocusedThemeIndex(0);
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        setShowDropdown(false);
        setPendingSelection(null);
      }
    } else if (e.key === "Backspace" && inputValue === "" && props.mentions.length > 0) {
      // Remove last tag when backspace is pressed with empty input
      e.preventDefault();
      if (activeTagIndex !== null) {
        // Remove the selected tag
        const mention = props.mentions[activeTagIndex];
        props.onMentionRemove(mention);
        setActiveTagIndex(null);
      } else {
        // Select the last tag
        setActiveTagIndex(props.mentions.length - 1);
      }
    } else if ((e.key === "ArrowLeft" || e.key === "ArrowRight") && inputValue === "" && props.mentions.length > 0) {
      // Cycle sentiment on the active tag, or the last tag if none is active
      e.preventDefault();
      const targetIndex = activeTagIndex ?? props.mentions.length - 1;
      const mention = props.mentions[targetIndex];
      if (activeTagIndex === null) {
        setActiveTagIndex(targetIndex);
      }
      if (mention) {
        const newSentiment =
          e.key === "ArrowRight"
            ? getNextItemInArray([1, -1, 0] as const, mention.sentiment)
            : getPreviousItemInArray([1, -1, 0] as const, mention.sentiment);
        props.onMentionRemove(mention);
        props.onMentionAdd({
          theme_id: mention.theme_id,
          sentiment: newSentiment as ApiResponseMention["sentiment"],
        });
      }
    }
  };

  const handleHoverStart = (themeId: string) => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }
    setHoveredThemeId(null);
    const timeout = setTimeout(() => {
      setHoveredThemeId(themeId);
    }, 1000);
    setHoverTimeout(timeout);
  };

  const handleHoverEnd = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
    setHoveredThemeId(null);
  };

  React.useEffect(() => {
    if (!showDropdown) {
      handleHoverEnd();
    }
  }, [showDropdown]);

  React.useEffect(() => {
    if (!showDropdown || filteredThemes.length === 0) {
      return;
    }
    const focusedTheme = filteredThemes[focusedThemeIndex];
    if (focusedTheme?.id) {
      handleHoverStart(focusedTheme.id);
    }
  }, [focusedThemeIndex, filteredThemes, showDropdown]);

  const handleTagClick = (index: number) => {
    setActiveTagIndex(index);
    inputRef.current?.focus();
  };

  const handleRemoveTag = (mention: ApiResponseMention, e: React.MouseEvent) => {
    e.stopPropagation();
    props.onMentionRemove(mention);
    setActiveTagIndex(null);
    inputRef.current?.focus();
  };

  return (
    <div className="space-y-1">
      <div className="relative">
        {/* Input container that looks like a single input field */}
        <div
          ref={containerRef}
          className="flex flex-wrap items-center gap-1 min-h-[2.5rem] w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-within:border-ring/50 transition-colors"
          onClick={() => inputRef.current?.focus()}
        >
          {/* Render tags inline */}
          {props.mentions.map((mention, index) => (
            <span
              key={`${mention.theme_id}-${mention.sentiment}`}
              onClick={() => handleTagClick(index)}
              className={cn(
                "inline-flex items-center gap-1 px-2 py-1 text-sm rounded-md transition-all cursor-pointer",
                getSentimentColor(mention.sentiment),
                activeTagIndex === index && "shadow-sm",
                activeTagIndex === index && mention.sentiment === 1 && "shadow-green-600/50 dark:shadow-green-500/50",
                activeTagIndex === index && mention.sentiment === -1 && "shadow-red-500/50 dark:shadow-red-400/50",
                activeTagIndex === index && mention.sentiment === 0 && "shadow-gray-500/50 dark:shadow-gray-400/50"
              )}
            >
              {getLabel(mention.theme_id, props.themes, categoriesMap)}
              <button
                type="button"
                onClick={(e) => handleRemoveTag(mention, e)}
                className="hover:opacity-70 transition-opacity ml-1"
              >
                <span className="text-base leading-none">×</span>
              </button>
            </span>
          ))}

          {/* Input field */}
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (inputValue) setShowDropdown(true);
              setActiveTagIndex(null);
            }}
            placeholder={props.mentions.length === 0 ? "Start typing to find themes..." : ""}
            className="flex-1 min-w-[120px] bg-transparent outline-none placeholder:text-muted-foreground"
          />
        </div>

        {/* Custom dropdown */}
        {showDropdown && themesByCategory.length > 0 && (
          <div className="absolute z-50 w-full mt-1 rounded-md border bg-popover shadow-md">
            <Command shouldFilter={false}>
              <CommandList>
                {themesByCategory.map((categoryGroup) => {
                  // Calculate the starting index for this category's themes
                  const categoryStartIndex = filteredThemes.findIndex(
                    t => t.categoryId === categoryGroup.categoryId
                  );

                  return (
                    <CommandGroup
                      key={categoryGroup.categoryId}
                      heading={categoryGroup.categoryName}
                    >
                      {categoryGroup.themes.map((theme, themeIndexInCategory) => {
                        const globalIndex = categoryStartIndex + themeIndexInCategory;
                        const isFocused = globalIndex === focusedThemeIndex;
                        const hasPendingSentiment = pendingSelection?.themeId === theme.id;
                        const sentiment = hasPendingSentiment ? pendingSelection.sentiment : 1;
                        const isHovered = hoveredThemeId === theme.id;

                        return (
                          <CommandItem
                            key={theme.id}
                            onSelect={() => {
                              props.onMentionAdd({
                                theme_id: Number(theme.id),
                                sentiment,
                              });
                              setInputValue("");
                              setShowDropdown(false);
                              setPendingSelection(null);
                              setFocusedThemeIndex(0);
                            }}
                            className={cn(
                              "cursor-pointer ml-4",
                              isFocused && getSentimentColor(sentiment)
                            )}
                            onMouseEnter={() => handleHoverStart(theme.id)}
                            onMouseLeave={handleHoverEnd}
                          >
                            <div
                              className="grid items-center w-full gap-3 min-w-0"
                              style={{
                                gridTemplateColumns: `${Math.max(maxLabelLength, 12)}ch 1fr auto`,
                              }}
                            >
                              <span className="truncate">{theme.label}</span>
                              <span className="truncate text-muted-foreground">
                                {theme.description}
                              </span>
                              {theme.description ? (
                                <Popover open={isHovered} onOpenChange={() => {}}>
                                  <PopoverTrigger asChild>
                                    <span
                                      className="inline-flex h-4 w-4 items-center justify-center rounded-full border text-[10px] text-muted-foreground"
                                      aria-label="Theme description"
                                      onMouseEnter={() => handleHoverStart(theme.id)}
                                      onMouseLeave={handleHoverEnd}
                                    >
                                      i
                                    </span>
                                  </PopoverTrigger>
                                  <PopoverContent
                                    side="right"
                                    onOpenAutoFocus={(event) => event.preventDefault()}
                                    onCloseAutoFocus={(event) => event.preventDefault()}
                                  >
                                    <div className="space-y-2">
                                      <h4 className="font-semibold text-sm">
                                        {theme.label}
                                      </h4>
                                      <div>
                                        <p className="text-xs font-medium text-muted-foreground mb-1">Description</p>
                                        <p className="text-sm text-foreground">
                                          {theme.description}
                                        </p>
                                      </div>
                                    </div>
                                  </PopoverContent>
                                </Popover>
                              ) : null}
                            </div>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  );
                })}
              </CommandList>
            </Command>
          </div>
        )}
      </div>

      <div className="text-xs text-muted-foreground">
        {showDropdown ? (
          <>Use ↑↓ to navigate, ← → to cycle sentiment (Positive → Negative → Neutral), Enter to confirm</>
        ) : activeTagIndex !== null ? (
          <>Use ← → to cycle sentiment, Backspace to remove</>
        ) : null}
      </div>
    </div>
  );
};
